import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiArrowUpRight, FiClock, FiEye, FiHelpCircle } from 'react-icons/fi';
import SponsoredSection from '../components/SponsoredSection';
import TxAdditionalInfoPopover from '../components/TxAdditionalInfoPopover';
import CopyButton from '../components/transaction/CopyButton';
import { useRecentAllTransactions } from '../hooks/useRecentAllTransactions';

function SkeletonRows({ count = 8 }) {
  return Array.from({ length: count }).map((_, i) => (
    <div key={i} className="tx-row tx-skeleton-row" aria-hidden>
      <span className="tx-leading-col"><span className="tx-skeleton tx-skeleton--circle" /></span>
      <span className="tx-skeleton-cell"><span className="tx-skeleton tx-skeleton--wide" /></span>
      <span className="tx-skeleton-cell"><span className="tx-skeleton tx-skeleton--med" /></span>
      <span className="tx-skeleton-cell"><span className="tx-skeleton tx-skeleton--short" /></span>
      <span className="tx-skeleton-cell"><span className="tx-skeleton tx-skeleton--med" /></span>
    </div>
  ));
}

function TransactionsListPage() {
  const { transactions, loading, error } = useRecentAllTransactions();
  const [addInfo, setAddInfo] = useState(null);

  const closeAddInfo = useCallback(() => setAddInfo(null), []);

  useEffect(() => {
    if (!addInfo) return undefined;
    const onDocMouseDown = (e) => {
      if (e.target.closest?.('.tx-addinfo-popover') || e.target.closest?.('.tx-eye')) return;
      closeAddInfo();
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') closeAddInfo(); };
    const onScroll = () => closeAddInfo();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [addInfo, closeAddInfo]);

  const handleEyeClick = (event, tx) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAddInfo((cur) => (cur?.tx?.signature === tx.signature ? null : { tx, anchor: rect }));
  };

  return (
    <main className="sections tx-list-page">
      <header className="tx-list-head">
        <h1>Transactions</h1>
        <p className="tx-list-sub">Latest transactions across all networks. Auto-refreshes every 15s.</p>
      </header>

      <SponsoredSection />

      <section className="panel panel-full">
        <div className="panel-head">
          <h3>All Transactions</h3>
        </div>
        <div className="tx-table-scroll">
          {loading ? (
            <div className="tx-table">
              <div className="tx-row tx-head">
                <span className="tx-leading-col"><FiHelpCircle /></span>
                <span>Signature</span>
                <span className="tx-time-head">Time <FiClock /></span>
                <span>Block</span>
                <span>Action</span>
              </div>
              <SkeletonRows count={10} />
            </div>
          ) : null}

          {error && !loading ? <p className="tx-table-error">{error}</p> : null}

          {!loading && !error ? (
            <div className="tx-table">
              <div className="tx-row tx-head">
                <span className="tx-leading-col"><FiHelpCircle /></span>
                <span>Signature</span>
                <span className="tx-time-head">Time <FiClock /></span>
                <span>Block</span>
                <span>Action</span>
              </div>
              {transactions.map((tx, idx) => (
                <div key={`${tx.signature}-${idx}`} className="tx-row">
                  <span className="tx-leading-col">
                    <button
                      type="button"
                      className={`tx-eye ${addInfo?.tx?.signature === tx.signature ? 'is-active' : ''}`}
                      aria-expanded={addInfo?.tx?.signature === tx.signature}
                      aria-haspopup="dialog"
                      aria-label="Additional transaction info"
                      onClick={(e) => handleEyeClick(e, tx)}
                    >
                      <FiEye />
                    </button>
                  </span>
                  <span className="tx-signature-cell">
                    {!tx.success ? <FiAlertCircle className="tx-alert-dot" /> : null}
                    <Link to={`/tx/${encodeURIComponent(tx.signature)}`} className="tx-signature-link">
                      {tx.displaySig}
                    </Link>
                    <CopyButton text={tx.signature} className="tx-copy-icon-btn" label="Copy signature" />
                  </span>
                  <span>{tx.time}</span>
                  <Link to={`/tx/${tx.block}`} className="tx-block-link">
                    {tx.block}
                  </Link>
                  <span className="tx-action-cell">
                    <span className="tx-action-pill">
                      <FiArrowUpRight />
                      {tx.action}
                    </span>
                    {tx.badge ? <span className="tx-plus-pill">{tx.badge}</span> : null}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {addInfo ? (
        <TxAdditionalInfoPopover
          key={addInfo.tx.signature}
          anchor={addInfo.anchor}
          signature={addInfo.tx.signature}
          tx={addInfo.tx}
          onClose={closeAddInfo}
        />
      ) : null}
    </main>
  );
}

export default TransactionsListPage;
